import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

import { fetchConnectionsPaginated } from '../../../../redux/slices/connectionsSlice';

const toActionEntity = (row: any): ActionEntity => ({
  type: 'connection',
  id: row.connection_id,
  name: row.connection_name,
  connectionType: row.connection_type,
});

const ListConnections = () => {
  const {
    items: connections,
    config: headerConfig = [],
    filtersConfig = [],
    pagination = {
      total_records: 0,
      total_pages: 0,
      current_page: 1,
      limit: 10,
      offset: 0,
    },
    error,
    loading,
  } = useSelector((state: RootState) => state.connections);

  const userData = useSelector((state: RootState) => state.auth.userData);

  const pageName = 'connections';
  const [showSidebar, setShowSidebar] = useState(false);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    page,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchConnectionsPaginated,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'addToTransportConnection', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(connections), [connections]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.connection_name),
  });

  const selectionEntities = table.getSelectedRowModel().rows.map((row: any) => toActionEntity(row.original));
  // Toolbar mutations (bulk delete) act on the current selection; clear it after
  // so a stale selection can't fire a second action against already-removed rows.
  const toolbarActions = getActions({
    entities: selectionEntities,
    refresh: () => {
      table.resetRowSelection();
      refresh();
    },
  });

  const isTableLoading = loading.fetchConnectionsPaginated;

  return (
    <>
      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName={pageName}
        columns={headerConfig.map((c) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error as any}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={page}
        totalPages={(pagination as any).total_pages || 0}
        totalItems={(pagination as any).total_records || 0}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        toolbarButtons={
          <>
            <ActionButton actions={toolbarActions} action="delete" label="Delete Selected" />
            <ActionButton actions={toolbarActions} action="addToTransportConnection" label="Add selected to transport" />
            <ActionButton actions={toolbarActions} action="createConnection" />
          </>
        }
        isLoading={isTableLoading}
        isSilentRefresh={isSilentRefresh}
      />
    </>
  );
};

export default ListConnections;
